import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, hasRole } from "../../context/AuthContext";
import { dashboardAPI, attendanceViewApi, errorMessage } from "../adminApi";
import { Card, Loading, ErrorState, Avatar } from "../components/ui";
import { Icon } from "../components/icons";
import AttendanceViewModal, { useAttendanceDetail } from "../components/AttendanceDetailModal";
import { formatTime, formatMinutes } from "../format";

/**
 * RINGKASAN admin — "kondisi tim hari ini". Data di-scope backend
 * (/attendance-view/today): HR lihat semua karyawan, MANAGER/PIC hanya timnya.
 * Role non-manajerial hanya melihat strip absen pribadi.
 */

const MANAJERIAL = ["SUPER_ADMIN", "HR_ADMIN", "MANAGER", "PIC"];
const DAY_SHORT = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];

const longDate = (iso) =>
  new Date(`${iso}T00:00:00`).toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" });

/** Angka utama + selisih vs kemarin (opsional). */
function Stat({ label, value, sub, delta, goodWhenUp = true }) {
  const hasDelta = delta != null && delta !== 0;
  const good = hasDelta && (delta > 0) === goodWhenUp;
  return (
    <div className="px-5 py-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="text-2xl font-semibold text-slate-900 tabular-nums">{value}</span>
        {sub && <span className="text-sm text-slate-400">{sub}</span>}
      </div>
      <div className="mt-1 h-4 text-[11px]">
        {hasDelta ? (
          <span className={good ? "text-emerald-600" : "text-brand-600"}>
            {delta > 0 ? "+" : ""}{delta} vs kemarin
          </span>
        ) : delta === 0 ? (
          <span className="text-slate-400">sama dgn kemarin</span>
        ) : null}
      </div>
    </div>
  );
}

/** Kartu dengan judul kecil + aksi "lihat semua". */
function Panel({ title, action, onAction, children, className = "" }) {
  return (
    <Card className={`p-5 flex flex-col ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
        {action && (
          <button onClick={onAction} className="text-xs text-slate-500 hover:text-brand-600 inline-flex items-center gap-0.5">
            {action} <Icon.ChevronRight size={14} />
          </button>
        )}
      </div>
      {children}
    </Card>
  );
}

function ActionRow({ count, label, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full flex items-center gap-3 py-3 border-b border-slate-100 last:border-0 text-left group"
    >
      <span className={`h-2 w-2 rounded-full shrink-0 ${count > 0 ? "bg-brand-600" : "bg-slate-300"}`} />
      <span className="flex-1 text-sm text-slate-700">
        <b className="text-slate-900 tabular-nums">{count}</b> {label}
      </span>
      <Icon.ChevronRight size={16} className="text-slate-300 group-hover:text-brand-600" />
    </button>
  );
}

/** Bar chart kehadiran 7 hari (CSS murni). */
function TrendChart({ trend, teamSize }) {
  const max = Math.max(teamSize, ...trend.map((t) => t.present), 1);
  return (
    <div className="flex-1 flex items-end gap-2 min-h-[140px]">
      {trend.map((t, idx) => {
        const isToday = idx === trend.length - 1;
        const pct = Math.round((t.present / max) * 100);
        return (
          <div key={t.date} className="flex-1 flex flex-col items-center gap-1.5 h-full" title={`${t.present} hadir · ${t.late} telat`}>
            <span className="text-[11px] text-slate-500 tabular-nums">{t.present}</span>
            <div className="w-full flex-1 flex items-end">
              <div
                className={`w-full rounded-md ${isToday ? "bg-brand-600" : "bg-slate-200"}`}
                style={{ height: `${Math.max(pct, 2)}%` }}
              />
            </div>
            <span className={`text-[11px] ${isToday ? "text-slate-900 font-semibold" : "text-slate-400"}`}>
              {DAY_SHORT[new Date(`${t.date}T00:00:00`).getDay()]}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function PersonRow({ row, right, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className="w-full flex items-center gap-3 py-2.5 border-b border-slate-100 last:border-0 text-left enabled:hover:bg-slate-50 -mx-2 px-2 rounded-lg"
    >
      <Avatar name={row.employee_name} size={32} className="!bg-slate-100 !text-slate-600 !font-semibold" />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-800 truncate">{row.employee_name}</div>
        <div className="text-[11px] text-slate-500 truncate">{[row.job_title, row.department].filter(Boolean).join(" · ") || "-"}</div>
      </div>
      {right}
    </button>
  );
}

function Empty({ children }) {
  return <div className="flex-1 flex items-center justify-center py-6 text-sm text-slate-400">{children}</div>;
}

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isManagerial = hasRole(user, ...MANAJERIAL);
  const [me, setMe] = useState(null);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { selected, detail, open: openDetail, close: closeDetail } = useAttendanceDetail();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [mine, today] = await Promise.all([
        dashboardAPI.summary(),
        isManagerial ? attendanceViewApi.today() : Promise.resolve(null),
      ]);
      setMe(mine);
      setTeam(today);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <Loading label="Memuat ringkasan…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  const att = me?.attendance_today;
  const goAttendance = () => navigate("/admin/attendance");
  const t = team?.today;
  const y = team?.yesterday;
  const presentPct = team?.team_size ? Math.round((t.present / team.team_size) * 100) : 0;

  return (
    <div className="h-full overflow-auto">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-2 mb-5">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Halo, {user?.name || user?.username}</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {team ? (team.scope === "all" ? "Semua karyawan" : "Tim kamu") + ` · ${team.team_size} orang` : "Ringkasan"}
          </p>
        </div>
        <div className="text-sm text-slate-500">{longDate(me?.date || new Date().toISOString().slice(0, 10))}</div>
      </div>

      {team && (
        <>
          {/* Angka utama hari ini */}
          <Card className="grid grid-cols-2 md:grid-cols-5 divide-x divide-y md:divide-y-0 divide-slate-100 mb-4 overflow-hidden">
            <Stat label="Hadir" value={t.present} sub={`/ ${team.team_size} · ${presentPct}%`} delta={t.present - y.present} />
            <Stat label="Telat" value={t.late} delta={t.late - y.late} goodWhenUp={false} />
            <Stat label="Belum Absen" value={t.absent} />
            <Stat label="Cuti" value={t.on_leave} />
            <Stat label="WFH" value={t.wfh} delta={t.wfh - y.wfh} />
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            <Panel title="Perlu Tindakan">
              <ActionRow
                count={team.approvals.leave_pending}
                label="pengajuan cuti menunggu approval"
                onClick={() => navigate("/admin/approval/leave")}
              />
              <ActionRow
                count={team.approvals.attendance_anomalies}
                label="anomali absensi"
                onClick={() => navigate("/admin/attendance/anomalies")}
              />
            </Panel>
            <Panel title="Kehadiran 7 Hari" className="lg:col-span-2">
              <TrendChart trend={team.trend} teamSize={team.team_size} />
            </Panel>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            <Panel title={`Belum Absen Hari Ini (${t.absent})`} action={t.absent > 0 ? "Lihat semua" : null} onAction={goAttendance}>
              {team.absent_list.length === 0 ? (
                <Empty>Semua sudah absen</Empty>
              ) : (
                team.absent_list.map((r) => <PersonRow key={r.user_id} row={r} />)
              )}
            </Panel>
            <Panel title={`Telat Hari Ini (${team.late_total})`} action={team.late_total > 0 ? "Lihat semua" : null} onAction={goAttendance}>
              {team.late_list.length === 0 ? (
                <Empty>Tidak ada yang telat</Empty>
              ) : (
                team.late_list.map((r) => (
                  <PersonRow
                    key={r.id}
                    row={r}
                    onClick={() => openDetail(r)}
                    right={
                      <div className="text-right shrink-0">
                        <div className="text-sm font-medium text-slate-800 tabular-nums">{formatTime(r.check_in)}</div>
                        {r.late_minutes > 0 && <div className="text-[11px] text-brand-600">+{formatMinutes(r.late_minutes)}</div>}
                      </div>
                    }
                  />
                ))
              )}
            </Panel>
          </div>
        </>
      )}

      {/* Absen pribadi */}
      <Card className="px-5 py-3.5 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        <span className="text-slate-500">Absen kamu hari ini</span>
        <span className="text-slate-800">
          Check-in <b className="tabular-nums">{formatTime(att?.check_in)}</b>
        </span>
        <span className="text-slate-800">
          Check-out <b className="tabular-nums">{att?.check_out ? formatTime(att.check_out) : "belum"}</b>
        </span>
        {!att?.check_in && <span className="text-brand-600">Belum absen</span>}
      </Card>

      <AttendanceViewModal open={!!selected} row={selected} detail={detail} onClose={closeDetail} onDeleted={() => { closeDetail(); load(); }} />
    </div>
  );
}
